// Theme tokens for styled-components, mapped to AntD tokens where appropriate
// Supports both light and dark modes with swapped secondary/tertiary colors

import { theme as antdTheme } from 'antd';
import { hexToRgba } from '@utils/colors';

const baseTheme = {
    colors: {
        primary: '#F04A4A',
        success: '#52c41a',
        warning: '#faad14',
        error: '#ff4d4f',
        info: '#1890ff',
        link: '#1890ff',
        linkHover: '#0077be'
    },
    fonts: {
        lufga: '\'Lufga\', -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, \'Helvetica Neue\', Arial, sans-serif',
        figtree: '\'Figtree\', -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, \'Helvetica Neue\', Arial, sans-serif',
        family: '\'Figtree\', -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, \'Helvetica Neue\', Arial, sans-serif',
        weights: {
            thin: 100,
            extraLight: 200,
            light: 300,
            regular: 400,
            medium: 500,
            semiBold: 600,
            bold: 700,
            extraBold: 800,
            black: 900
        },
        figtreeWeights: {
            light: 300,
            regular: 400,
            medium: 500,
            semiBold: 600,
            bold: 700,
            extraBold: 800,
            black: 900
        }
    },
    spacing: {
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '24px',
        xl: '32px',
        xxl: '48px'
    },
    borderRadius: {
        sm: '4px',
        md: '8px',
        lg: '12px',
        xl: '16px'
    },
    breakpoints: {
        xs: '480px',
        sm: '576px',
        md: '768px',
        lg: '992px',
        xl: '1200px',
        xxl: '1600px'
    },
    zIndex: {
        dropdown: 1050,
        sticky: 1020,
        fixed: 1030,
        modal: 1040,
        popover: 1060,
        tooltip: 1070
    }
};

// Light theme - secondary: #fafafa, tertiary: #f5f5f5
const lightTheme = {
    ...baseTheme,
    colors: {
        ...baseTheme.colors,
        text: {
            primary: '#F04A4A',
            secondary: 'rgba(0, 0, 0, 0.65)',
            disabled: 'rgba(0, 0, 0, 0.25)'
        },
        background: {
            default: '#ffffff',
            secondary: '#fafafa', // Light mode secondary
            tertiary: '#f5f5f5'   // Light mode tertiary
        },
        border: {
            default: '#d9d9d9',
            light: '#f0f0f0'
        }
    }
};

// Dark theme - secondary and tertiary are swapped
const darkTheme = {
    ...baseTheme,
    colors: {
        ...baseTheme.colors,
        text: {
            primary: '#F04A4A', // Primary stays same
            secondary: 'rgba(255, 255, 255, 0.65)',
            disabled: 'rgba(255, 255, 255, 0.25)'
        },
        background: {
            default: '#141414',
            secondary: '#1f1f1f', // Dark mode secondary (swapped from light tertiary)
            tertiary: '#262626'   // Dark mode tertiary (swapped from light secondary)
        },
        border: {
            default: '#434343',
            light: '#303030'
        }
    }
};

/**
 * Get theme based on mode
 */
export const getTheme = (mode = 'light', overrides = {}) => {
    const base = mode === 'dark' ? darkTheme : lightTheme;
    const primary = overrides?.primaryColor || base?.colors?.primary;
    if (!primary || typeof primary !== 'string') return base;
    return {
        ...base,
        colors: {
            ...base.colors,
            primary,
            text: {
                ...(base.colors?.text || {}),
                // keep text.primary aligned to primary accents in this theme system
                primary,
            },
        },
    };
};

// Default export (light theme for backward compatibility)
export const theme = lightTheme;

/**
 * Get Ant Design theme configuration based on mode
 */
export const getAntdTheme = (mode = 'light', overrides = {}) => {
    const currentTheme = getTheme(mode, overrides);
    
    const isDark = mode === 'dark';
    const fieldBg = isDark ? currentTheme.colors.background.secondary : '#ffffff';
    const elevatedBg = isDark ? currentTheme.colors.background.secondary : '#ffffff';

    return {
        defaultSize: 'large',
        token: {
            fontFamily: currentTheme.fonts.family,
            fontWeightStrong: 600,
            fontSize: 16,
            colorPrimary: currentTheme.colors.primary, // Always #F04A4A
            colorText: isDark ? 'rgba(255, 255, 255, 0.85)' : '#1f1f1f',
            colorTextHeading: isDark ? 'rgba(255, 255, 255, 0.85)' : '#1a1a1a',
            colorBgContainer: fieldBg,
            colorBgElevated: elevatedBg,
            colorBgLayout: isDark ? currentTheme.colors.background.default : currentTheme.colors.background.tertiary,
            colorBorder: isDark ? currentTheme.colors.border.default : '#d9d9d9',
            borderRadius: 6,
            borderRadiusSM: 6,
            borderRadiusLG: 6,
            borderRadiusXS: 6
        },
        components: {
            Typography: {
                fontFamily: currentTheme.fonts.figtree,
                titleFontFamily: currentTheme.fonts.lufga,
                headingFontFamily: currentTheme.fonts.lufga,
                fontWeightStrong: 700
            },
            Input: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: fieldBg,
                activeBg: fieldBg,
                // hoverBg: mode === 'light' ? '#f5f5f5' : '#1a1a1a',
                colorBorder: isDark ? currentTheme.colors.border.default : '#d9d9d9',
                colorText: isDark ? 'rgba(255, 255, 255, 0.9)' : '#1f1f1f',
                colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.35)',
            },
            InputPassword: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: fieldBg,
                activeBg: fieldBg,
                colorBorder: isDark ? currentTheme.colors.border.default : '#d9d9d9',
                colorText: isDark ? 'rgba(255, 255, 255, 0.9)' : '#1f1f1f',
                colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.35)',
            },
            Select: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: fieldBg,
                selectorBg: fieldBg,
                colorBorder: isDark ? currentTheme.colors.border.default : '#d9d9d9',
                colorText: isDark ? 'rgba(255, 255, 255, 0.9)' : '#1f1f1f',
                colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.35)',
            },
            Modal: {
                borderRadiusLG: 14,
                borderRadiusSM: 14,
                paddingContentHorizontalLG: 20,
                paddingMD: 16,
                paddingLG: 20,
                titleFontSize: 16,
                titleColor: isDark ? 'rgba(255, 255, 255, 0.88)' : '#1f1f1f',
                contentBg: elevatedBg,
                headerBg: elevatedBg,
                footerBg: elevatedBg,
            },
            Button: {
                size: 'large',
                borderRadius: 6,
                colorPrimary: currentTheme.colors.primary, // Always #F04A4A
                colorPrimaryHover: '#ff5d5d',
                colorPrimaryActive: '#d93d3d',
                defaultBg: mode === 'dark' ? '#434343' : '#747474',
                defaultBorderColor: mode === 'dark' ? '#434343' : '#747474',
                defaultColor: '#ffffff',
                defaultHoverBg: mode === 'dark' ? '#595959' : '#8b8b8b',
                defaultHoverBorderColor: mode === 'dark' ? '#595959' : '#8b8b8b',
                defaultActiveBg: mode === 'dark' ? '#303030' : '#616161',
                defaultActiveBorderColor: mode === 'dark' ? '#303030' : '#616161'
            },
            DatePicker: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : '#000000',
                activeBg: mode === 'light' ? '#ffffff' : '#000000',
                hoverBg: mode === 'light' ? '#f5f5f5' : '#1a1a1a',
                colorBorder: mode === 'dark' ? '#2C2C2C' : '#d9d9d9',
                colorText: mode === 'dark' ? '#ffffff' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
            },
            RangePicker: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : '#000000',
                activeBg: mode === 'light' ? '#ffffff' : '#000000',
                hoverBg: mode === 'light' ? '#f5f5f5' : '#1a1a1a',
                colorBorder: mode === 'dark' ? '#2C2C2C' : '#d9d9d9',
                colorText: mode === 'dark' ? '#ffffff' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
            },
            TimePicker: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : '#000000',
                activeBg: mode === 'light' ? '#ffffff' : '#000000',
                hoverBg: mode === 'light' ? '#f5f5f5' : '#1a1a1a',
                colorBorder: mode === 'dark' ? '#8A98A7' : '#d9d9d9',
                colorText: mode === 'dark' ? '#ffffff' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
            },
            Cascader: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : '#000000',
                selectorBg: mode === 'light' ? '#000000' : '#ffffff',
                colorBorder: mode === 'dark' ? '#2C2C2C' : '#d9d9d9',
                colorText: mode === 'dark' ? '#ffffff' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
            },
            AutoComplete: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : '#000000',
                activeBg: mode === 'light' ? '#ffffff' : '#000000',
                hoverBg: mode === 'light' ? '#f5f5f5' : '#1a1a1a',
                colorBorder: mode === 'dark' ? '#2C2C2C' : '#d9d9d9',
                colorText: mode === 'dark' ? '#ffffff' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
            },
            TreeSelect: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : '#000000',
                selectorBg: mode === 'light' ? '#000000' : '#ffffff',
                colorBorder: mode === 'dark' ? '#2C2C2C' : '#d9d9d9',
                colorText: mode === 'dark' ? '#ffffff' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
            },
            InputNumber: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : '#000000',
                activeBg: mode === 'light' ? '#ffffff' : '#000000',
                hoverBg: mode === 'light' ? '#f5f5f5' : '#1a1a1a',
                colorBorder: mode === 'dark' ? '#2C2C2C' : '#d9d9d9',
                colorText: mode === 'dark' ? '#ffffff' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
            },
            Mentions: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : '#000000',
                activeBg: mode === 'light' ? '#ffffff' : '#000000',
                hoverBg: mode === 'light' ? '#f5f5f5' : '#1a1a1a',
                colorBorder: mode === 'dark' ? '#2C2C2C' : '#d9d9d9',
                colorText: mode === 'dark' ? '#ffffff' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
            },
            InputOTP: {
                size: 'large',
                borderRadius: 8,
                fontSize: 14,
                colorBgContainer: mode === 'light' ? '#ffffff' : '#000000',
                activeBg: mode === 'light' ? '#ffffff' : '#000000',
                hoverBg: mode === 'light' ? '#f5f5f5' : '#1a1a1a',
                colorBorder: mode === 'dark' ? '#8A98A7' : '#d9d9d9',
                colorText: mode === 'dark' ? '#ffffff' : '#1f1f1f',
                colorTextPlaceholder: mode === 'dark' ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
            },
            Form: {
                size: 'large'
            },
            Pagination: {
                size: 'large'
            },
            Tabs: {
                itemActiveColor: '#ffffff',
                itemSelectedColor: '#ffffff',
                itemSelectedBg: mode === 'dark' ? '#434343' : '#292929',
                itemHoverColor: mode === 'dark' ? '#595959' : '#292929',
                inkBarColor: currentTheme.colors.primary, // Always #F04A4A
                cardBg: mode === 'dark' ? currentTheme.colors.background.secondary : '#292929'
            },
            Menu: {
                itemSelectedBg: (() => {
                    const isDark = mode === 'dark';
                    return hexToRgba(currentTheme.colors.primary, isDark ? 0.16 : 0.10);
                })(),
                itemHoverBg: mode === 'dark' ? currentTheme.colors.background.tertiary : '#f5f5f5',
                itemActiveBg: (() => {
                    const isDark = mode === 'dark';
                    return hexToRgba(currentTheme.colors.primary, isDark ? 0.12 : 0.08);
                })(),
                itemSelectedColor: currentTheme.colors.primary, // Always #F04A4A
                itemHoverColor: mode === 'dark' ? '#ffffff' : '#1f1f1f',
                itemColor: mode === 'dark' ? '#8A98A7' : 'rgba(0, 0, 0, 0.65)',
                subMenuItemBg: mode === 'dark' ? currentTheme.colors.background.secondary : '#ffffff'
            },
            Badge: {
                // Use tenant primary color for notification counts
                colorError: currentTheme.colors.primary,
                colorErrorHover: currentTheme.colors.primary,
            },
            Layout: {
                bodyBg: mode === 'dark' ? '#000000' : currentTheme.colors.background.tertiary,
                headerBg: mode === 'dark' ? '#141414' : '#ffffff',
                siderBg: mode === 'dark' ? '#141414' : '#ffffff'
            },
            Card: {
                borderRadius: 6,
                colorTextHeading: mode === 'dark' ? 'rgba(255, 255, 255, 0.85)' : '#1a1a1a',
                colorText: mode === 'dark' ? 'rgba(255, 255, 255, 0.85)' : '#1f1f1f'
            },
            Breadcrumb: {
                linkColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
                separatorColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
                itemColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
                lastItemColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)'
            },
            Table: {
                borderRadius: 6
            },
            Space: {
                size: 'large'
            }
        }
    };
};

/**
 * Full Ant Design ConfigProvider theme (algorithm + component font defaults).
 */
export const buildFinalAntdConfig = (mode = 'light', overrides = {}) => {
    const m = mode === 'dark' ? 'dark' : 'light';
    const currentTheme = getTheme(m, overrides);
    const antdThemeConfig = getAntdTheme(m, overrides);
    const algorithm = m === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;
    const processed = {};
    const components = antdThemeConfig.components || {};
    Object.keys(components).forEach((componentName) => {
        const config = components[componentName];
        processed[componentName] = {
            ...config,
            fontFamily: config.fontFamily || currentTheme.fonts.family
        };
    });
    return {
        ...antdThemeConfig,
        algorithm,
        components: processed
    };
};

export default theme;
